import type { ComponentChildren } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { ForgeItemEnhancement } from '@/controllers/forge-controller';
import { useClient } from '@/ui/context';
import { getItemMeta } from '@/utils/get-item-meta';
import { ItemRequirementLine } from './item-requirement-line';
import { bookWisdomRequirement } from '@/utils/summoning-requirement';

export function ItemOverview({ itemId, enhancement }: { itemId: number; enhancement?: ForgeItemEnhancement }) {
  const client = useClient(), record = client.getEifRecordById(itemId);
  const [summonWis, setSummonWis] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    setSummonWis(null);
    bookWisdomRequirement(client,itemId).then(value => { if(!cancelled)setSummonWis(value); }).catch(() => {});
    return () => { cancelled = true; };
  },[client,itemId]);
  if (!record) return <p>Item #{itemId}: details unavailable.</p>;
  const lines = [
    ...(enhancement?.elements.map(element => `${element.element} +${element.power}`) ?? []),
    ...(enhancement?.stats ?? []), ...(enhancement?.totals ?? []),
    ...getItemMeta(record, itemId, enhancement),
  ];
  return <div data-item-overview={itemId}>
    <p class='mb-1 font-semibold'>{enhancement?.display_name || record.name}</p>
    {lines.map((line, index) => <p key={index} class='mb-1 text-xs leading-snug'>
      <ItemRequirementLine line={line} item={record} client={client} />
    </p>)}
  </div>;
}

/** Inspection is separate from Buy/Sell/Craft; touching this never transacts. */
export function ItemDetails({ itemId, enhancement, children }: {
  itemId: number; enhancement?: ForgeItemEnhancement; children?: ComponentChildren;
}) {
  const client = useClient(), name = enhancement?.display_name || client.getEifRecordById(itemId)?.name || `Item #${itemId}`;
  const id = useId(), trigger = useRef<HTMLButtonElement>(null), panel = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false), [pinned, setPinned] = useState(false);
  const [position, setPosition] = useState({left:8,top:8});
  const open = hovered || pinned;
  const close = () => { setHovered(false); setPinned(false); };

  useLayoutEffect(() => { close(); }, [itemId]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!trigger.current?.contains(event.target as Node) && !panel.current?.contains(event.target as Node)) close();
    };
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') { close(); trigger.current?.blur(); } };
    window.addEventListener('pointerdown', outside);
    window.addEventListener('keydown', key);
    return () => { window.removeEventListener('pointerdown', outside); window.removeEventListener('keydown', key); };
  }, [open]);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const anchor = trigger.current?.getBoundingClientRect(), box = panel.current?.getBoundingClientRect();
      if (!anchor || !box) return;
      const left = Math.max(8, Math.min(anchor.left, window.innerWidth-box.width-8));
      const below = anchor.bottom+6;
      const top = Math.max(8, Math.min(below+box.height <= window.innerHeight-8 ? below : anchor.top-box.height-6, window.innerHeight-box.height-8));
      setPosition({left,top});
    };
    place(); window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, itemId, enhancement]);

  return <>
    <button ref={trigger} type='button' data-item-details={itemId}
      class='w-full rounded border border-base-content/20 px-2 py-1 text-xs hover:bg-base-content/10 focus-visible:outline focus-visible:outline-2'
      aria-label={`Details: ${name}`} aria-expanded={open} aria-controls={open ? id : undefined}
      onPointerEnter={event => { if (event.pointerType === 'mouse') setHovered(true); }}
      onPointerLeave={() => setHovered(false)}
      onFocus={event => { if (event.currentTarget.matches(':focus-visible')) setHovered(true); }}
      onBlur={() => setHovered(false)}
      onPointerDown={event => event.stopPropagation()}
      onClick={event => { event.stopPropagation(); setHovered(false); setPinned(value => !value); }}>
      {children ?? 'Stats & effects'}
    </button>
    {open && createPortal(<div ref={panel} id={id} role='dialog' aria-label={`${name} stats and effects`}
      class='rounded border border-base-content/30 bg-base-300 p-3 text-base-content shadow-xl'
      style={{position:'fixed',zIndex:10020,width:'min(320px, calc(100vw - 16px))',maxHeight:'calc(100dvh - 16px)',overflowY:'auto',overflowWrap:'anywhere',...position}}
      onPointerDown={event => event.stopPropagation()} onClick={event => event.stopPropagation()}>
      <button type='button' class='float-right ml-2 rounded px-2 py-1' aria-label='Close item details' onClick={() => { trigger.current?.focus(); close(); }}>×</button>
      <ItemOverview itemId={itemId} enhancement={enhancement} />
    </div>, document.getElementById('ui') ?? document.body)}
  </>;
}
